import { Component } from '@angular/core';
import { HttpHeaders, HttpClient, HttpResponse } from '@angular/common/http';
// import { timer } from 'rxjs';
import { Subscription } from 'rxjs';
import { timer } from 'rxjs';
import { MapService } from './map.service';
import { DataLayerService } from './data-layer.service';
import { ConfigService } from './config.service';
import { UserMessageService } from './user-message.service';
import { DataLayerPNGComponent } from './data-layer-png.component';

declare var L;

/*
 * data-layer-png-rainviewer defines a class to display png layers from https://www.rainviewer.com/api.html
 * This gets its own class because a list of available time-stamped images must be retrieved in order
 * to create the retrieval url.  By default, only the most recent radar image is displayed; however, if "loop" is set
 * to true, all available images are displayed.
 *  
 * Images are 10 minutes apart.  10 images represents 1.5 hours of weather radar data.
 *
 * Within the configuration file, rainviewer png layers can be included:
 * "overlayLayers": [
 * 		{"id": "radarLayerRainviewerLoop", "label": "Rainviewer Radar (loop)",
 * 			"url": "https://tilecache.rainviewer.com/v2/radar/{timestamp}/256/{z}/{x}/{y}/5/1_1.png",
 *			"class": "data-layer-png-rainviewer",
 *			"loop": true,
 *			"maxImages": 10,
 *			"opacity": ".4",
 *			"refresh": true },
 * ...
 * ]
 *
 */

@Component({
	selector: 'data-layer-png-rainviewer',
	standalone: false,
	template: `
		<div class="checkboxDiv">
			<label class="overlay-layer" title={{titleString}}><input type="checkbox" title={{titleString}} (click)="menuControlSelected($event)">{{htmlElementLabel}}</label>
			<app-single-legend [active]=active [legendUrl]=legendUrl></app-single-legend>
		</div>
		`,
	styleUrls: ['app.component.css']
})
export class DataLayerPNGRainviewerComponent extends DataLayerPNGComponent {

	timestampUrl;
	timestamps = [];
	urlArray = [];
	subscription: Subscription;
	timer;
	titleString;
	currentRadarIndex;
	lastRadar;	// holds the previous radar so it can be removed on looping
	loop = false; // whether to loop or not
	firstTime = false;  // firstTime is for non looping and a fresh image is available
	maxImages = 10;	// maximum number of radar images to display; from configuration file or the number available, which ever is smaller
	userDefinedMaxImages = 10;	// maximum number requested by user

	constructor(
		mapService: MapService,
		dataLayerService: DataLayerService,
		configService: ConfigService,
		userMessageService: UserMessageService,
		http: HttpClient) {

		super(mapService, dataLayerService, configService, userMessageService, http);
		this.timestampUrl = 'https://tilecache.rainviewer.com/api/maps.json';
		// Maybe add a check of if this.url == null, then set here.  That would allow
		// the user to use a different url (rainviewer has several).
		this.url = 'https://tilecache.rainviewer.com/v2/radar/{timestamp}/256/{z}/{x}/{y}/5/1_1.png';
		this.timer = timer(2000, 500);
		this.subscription = this.timer.subscribe ( t => {
			this.displayRadar();
		});
		this.currentRadarIndex = 0;

		this.titleString='Images 10 minutes apart. Activate and hover for more detailed information.';
	}

	menuControlSelected (event): void {
		if ( event.target.checked ) {
			this.activateLayer();
		} else {
			this.deactivateLayer();
		}

	}

	// refreshLayer first checks to see if the layer is active.  If so, activateLayer is called.
	refreshLayer() {
		if ( this.active === true ) {
			this.activateLayer();
		}
	}

	// Set options from configuration file.
	setData ( someData: any ) {
		super.setData( someData );
		if ( someData.hasOwnProperty('loop')) {
			this.loop = someData.loop;
		}
		if ( someData.hasOwnProperty('maxImages')) {
			this.userDefinedMaxImages = someData.maxImages;
		}
	}

	// First check whether the information has been read.
	// Display the png on the map and set the layer to active.
	activateLayer () {
		console.log('Activating ' + this.id + ' layer.');
		if ( this.mapLayer != null ) {
			this.mapService.map.removeLayer(this.mapLayer);
		}
		if ( this.value == null || this.refresh === true ) {
			// once the data file has been read, drawMarkers is automatically called because an observer has been established
			this.parseDataFile();
		}

		// activate legend as well
		const legendDisplay = this.mapService.getDataLayerLegendDisplay(this.id);
		if ( legendDisplay != null ) {
			legendDisplay['source'].next (true);
		}

		this.active = true;
	}

	// To deactivate the radar layer, both the lastRadar image and the current image, if available, are removed.
	deactivateLayer() {
		console.log('Deactivating ' + this.id + ' layer.');
		if ( this.active === true ) {
			this.mapService.map.removeLayer(this.mapLayer);
			if ( this.lastRadar != null ) {
				this.mapService.map.removeLayer(this.lastRadar);
			}
			this.active = false;
		}
		const legendDisplay = this.mapService.getDataLayerLegendDisplay(this.id);
		if ( legendDisplay != null ) {
			legendDisplay['source'].next (false);
		}
	}

	// Retrieving the radar data is a 2-step process because RainViewer's url includes a timestamp.
	// Step 1 is retrieving the list of available timestamps.
	// Step 2 is getting actual radar layer.
	parseDataFile () {
		console.log('In data-layer-png.parseDataFile.');
		if ( this.url.startsWith('http')) {
			const self = this;
			const requestTimestamps = new XMLHttpRequest();
			requestTimestamps.onload = function ( ) {
				if ( requestTimestamps.readyState === requestTimestamps.DONE && requestTimestamps.status === 200 ) {

					console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' +
						self.timestampUrl + ' for radar timestamps was successfully completed.');
					// console.log ( dataLayer );

					// first, get timestamp file content
					self.timestamps = JSON.parse(requestTimestamps.responseText);
					// console.log(timestamps);

					// Using the list of timestamps, create the array of images available.
					// First, clear the array.
					self.urlArray = [];
					// Loop through the timestamps array and create the radar images url array.
					for (let i = 0; i < self.timestamps.length; i++) {
						const timestamp = self.timestamps[i];
						self.urlArray.push ( self.url.replace('{timestamp}', timestamp));
					}
					// Put the current radar in self.value
					self.value = self.url.replace('{timestamp}', self.timestamps[self.timestamps.length - 1]);
					self.firstTime = true;
					self.maxImages = Math.min( self.userDefinedMaxImages, self.urlArray.length );
					self.currentRadarIndex = self.urlArray.length - self.maxImages;

					// set titlestring
					const lastTimeStamp = self.timestamps[self.timestamps.length - 1] * 1000;
					const lastDate = new Date(lastTimeStamp);
					if ( self.loop === false ) {
						self.titleString = 'Image from ' + lastDate.toLocaleString();
					} else {
						const firstTimeStamp = self.timestamps[self.currentRadarIndex] * 1000;
						const firstDate = new Date(firstTimeStamp);
						self.titleString = 'Images from ' + firstDate.toLocaleString() + ' to ' + lastDate.toLocaleString();
					}
				} else {
					console.log ( '[' + new Date().toLocaleTimeString() + '] Retrieving radar timestamps from ' +
						self.timestampUrl + ' was UNSUCCESSFUL.');
				}
			};
			requestTimestamps.open('get', this.timestampUrl, true );
			requestTimestamps.overrideMimeType('text/plain');
			console.log('[' + new Date().toLocaleTimeString() + '] Attempting to retrieve radar timestamps from ' + this.timestampUrl);
			requestTimestamps.send();
		} else {
			this.value = this.configService.baseDir + this.url + '?nocache=' + (new Date()).getTime();
		}
	}

	// Note:  This gets called as soon as the configuration file includes data-layer-png-rainviewer, whether the user clicks to
	// display or not.
	displayRadar() {
		if ( this.active === false || this.urlArray.length === 0 ) {
			return;
		}

		// Non-looping first.  If firstTime is true, display the layer.  firstTime is set to true when a new image is available and hasn't
		// been displayed yet.
		if ( this.loop === false ) {
			if ( this.firstTime === true ) {
				// remove previous radar
				if ( this.mapLayer != null ) {
					this.mapService.map.removeLayer(this.mapLayer);
				}
				this.mapLayer = L.tileLayer( this.urlArray[this.urlArray.length - 1], {
						opacity: this.opacity,
				}).addTo(this.mapService.map).bringToFront();
				this.firstTime = false;
			}
		} else { // looping:
			const validIndex = this.urlArray.length - 1;  // maximum valid Index
			const resetValue = validIndex + 6;  // 3 seconds past current radar image since each one represents 1/2 second.

			// If the currentRadarIndex is within the valid range of indices, display it.
			if ( this.currentRadarIndex <= validIndex ) {
				let tmpLayer;
				if ( this.mapLayer != null ) {
					tmpLayer = this.mapLayer;
				}
				this.mapLayer = L.tileLayer( this.urlArray[this.currentRadarIndex], {
						opacity: this.opacity,
				}).addTo(this.mapService.map).bringToFront();

				// Remove what was the previous layer.
				if ( this.lastRadar != null ) {
					this.mapService.map.removeLayer(this.lastRadar);
				}
				// Set the previous layer the lastRadar for future removal.
				this.lastRadar = tmpLayer;
			}

			// If the currentRadarIndex is just past the validIndex range, remove the lastRadar image.
			// After this, there will be several seconds of resting on the current radar image.
			// The previous radar image must be removed.
			if ( this.currentRadarIndex === validIndex + 1 ) {
				if ( this.lastRadar != null ) {
					this.mapService.map.removeLayer(this.lastRadar);
					this.lastRadar = null;
				}
			}

			// If currentRadarImage has finally reached the resetValue, reset it to
			// the first desired image index and remove the current image from the map.
			this.currentRadarIndex++;
			if ( this.currentRadarIndex === resetValue ) {
				// reset to first desired index
				this.currentRadarIndex = this.urlArray.length - this.maxImages;
				if ( this.mapLayer != null ) {
					this.mapService.map.removeLayer(this.mapLayer);
					this.mapLayer = null;
				}
			}
		}
	}
}
